import {DeviceType} from "../components/DeviceTableToolbar"
import {RadarVersionEntry} from "../types/shadow"

export function versionToDate(version: number): {
	year: number
	month: number
	day: number
	hour: number
	minute: number
} {
	const MINS_PER_HOUR = 60
	const MINS_PER_DAY = 24 * 60
	const MINS_PER_MONTH = 31 * MINS_PER_DAY
	const MINS_PER_YEAR = 12 * MINS_PER_MONTH

	let remaining = version
	const year = Math.floor(remaining / MINS_PER_YEAR)
	remaining %= MINS_PER_YEAR

	const month = Math.floor(remaining / MINS_PER_MONTH)
	remaining %= MINS_PER_MONTH

	const day = Math.floor(remaining / MINS_PER_DAY)
	remaining %= MINS_PER_DAY

	const hour = Math.floor(remaining / MINS_PER_HOUR)
	const minute = remaining % MINS_PER_HOUR

	return {year, month, day, hour, minute}
}

export function formatVersionDate(version: number): string {
	const {year, month, day, hour, minute} = versionToDate(version)

	return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day
		.toString()
		.padStart(2, "0")} ${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
}

export const formatSoftwareVersion = (softwareVersion: string): string => {
	if (!softwareVersion) return "N/A"
	const versionMatch = softwareVersion.match(/^(\d+\.\d+\.\d+)/)
	return versionMatch ? versionMatch[1] : softwareVersion
}

export const parseRadarFirmwareVersions = (
	radarVersions: string | number[] | undefined,
): Array<{text: string; raw: number}> => {
	if (!radarVersions) return []

	let versions: number[] = []

	if (typeof radarVersions === "string") {
		if (radarVersions === "N/A" || !radarVersions.trim()) return []

		try {
			if (radarVersions.startsWith("[") && radarVersions.endsWith("]")) {
				const parsed = JSON.parse(radarVersions)
				if (Array.isArray(parsed)) {
					versions = parsed.filter((v) => typeof v === "number" && !isNaN(v))
				} else {
					return []
				}
			} else if (radarVersions.includes(",")) {
				versions = radarVersions
					.split(",")
					.map((v) => parseInt(v.trim()))
					.filter((v) => !isNaN(v))
			} else {
				const singleVersion = parseInt(radarVersions)
				if (!isNaN(singleVersion)) {
					versions = [singleVersion]
				} else {
					return []
				}
			}
		} catch {
			return []
		}
	} else if (Array.isArray(radarVersions)) {
		versions = radarVersions.filter((v) => typeof v === "number" && !isNaN(v))
	}

	return versions.map((version) => ({
		text: formatVersionDate(version),
		raw: version,
	}))
}

export const parseRadarFwStatusBooleans = (radarFwStatus: string | undefined): boolean[] | null => {
	if (!radarFwStatus || radarFwStatus === "N/A") return null

	const lower = radarFwStatus.toLowerCase()

	const colonEntries = radarFwStatus.match(/\d+:(latest|outdated|true|false)/gi)
	if (colonEntries) {
		return colonEntries.map((entry) => {
			const val = entry.split(":")[1].toLowerCase()
			return val === "latest" || val === "true"
		})
	}

	if (lower === "latest" || lower === "true") return [true]
	if (lower === "outdated" || lower === "false") return [false]

	return null
}

export const buildDeviceInfo = (
	softwareVersionRaw: string,
	radarVersionsRaw: string | number[] | undefined,
	radarFwStatus: string | undefined,
	fwStatus: string | undefined,
) => {
	const radarVersionsParsed = parseRadarFirmwareVersions(radarVersionsRaw)
	const radarStatusBooleans = parseRadarFwStatusBooleans(radarFwStatus)

	const radarVersions: RadarVersionEntry[] = radarVersionsParsed.map((v, idx) => ({
		text: `Radar ${idx}: ${v.text}`,
		isLatest: radarStatusBooleans ? (radarStatusBooleans[idx] ?? false) : null,
	}))

	return {
		softwareVersion: formatSoftwareVersion(softwareVersionRaw),
		radarVersions,
		radarFwStatus: radarFwStatus ?? "N/A",
		fwStatus: fwStatus ?? "N/A",
	}
}

export const getTimeSinceLastUpdate = (lastUpdateTime: Date | null): string => {
	if (!lastUpdateTime) return ""

	const now = new Date()
	const diff = now.getTime() - lastUpdateTime.getTime()
	const seconds = Math.floor(diff / 1000)

	if (seconds < 60) return `${seconds}s ago`
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
	return `${Math.floor(seconds / 3600)}h ago`
}

export const getDeviceType = (softwareVersion: string): DeviceType => {
	const versionUpper = softwareVersion.toUpperCase()
	if (versionUpper.includes("ANGEL")) return "angel"
	if (versionUpper.includes("FALCON")) return "falcon"
	return "unknown"
}

export const parseRadarVersions = (
	radarVersionsHtml: string,
	radarStatus: boolean[] | null,
): RadarVersionEntry[] => {
	if (!radarVersionsHtml || radarVersionsHtml === "N/A") return []

	const parser = new DOMParser()
	const doc = parser.parseFromString(radarVersionsHtml, "text/html")
	const spans = doc.querySelectorAll("span")

	const versions: RadarVersionEntry[] = []

	spans.forEach((span, index) => {
		const text = span.textContent || ""
		const isLatest = radarStatus ? radarStatus[index] === true : false
		versions.push({text, isLatest})
	})

	return versions
}

export const formatLastSeen = (timestamp?: string): string => {
	if (!timestamp) return "Never"

	const date = new Date(timestamp)
	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffMins = Math.floor(diffMs / (1000 * 60))

	if (diffMins < 1) return "Just now"
	if (diffMins < 60) return `${diffMins}m ago`
	if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
	return date.toLocaleDateString()
}

export const formatDataSize = (size?: number): string => {
	if (!size) return "No data"
	if (size < 1024) return `${size} B`
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
	return `${(size / (1024 * 1024)).toFixed(1)} MB`
}
